// theme.js — Dark/light toggle with clip-circle reveal

import { updateCanvasTheme } from './canvas.js';

const STORAGE_KEY = 'rl-theme';
const DARK_THEME_COLOR = '#070c09';
const LIGHT_THEME_COLOR = '#f4f7f2';
const THEME_PING_DURATION_MS = 520;
const THEME_PING_BASE_SIZE_PX = 18;

let isThemeTransitioning = false;

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getThemePingColor(theme) {
  if (theme === 'light') {
    return '#1a5c38';
  }

  return '#00e68a';
}

function launchThemePing(originX, originY, nextTheme, onComplete) {
  if (prefersReducedMotion()) {
    onComplete();
    return;
  }

  const ping = document.createElement('div');
  const maxRadius = Math.hypot(
    Math.max(originX, window.innerWidth - originX),
    Math.max(originY, window.innerHeight - originY)
  );
  const targetScale = (maxRadius * 2) / THEME_PING_BASE_SIZE_PX;

  ping.dataset.themePing = 'true';
  Object.assign(ping.style, {
    position: 'fixed',
    left: `${originX}px`,
    top: `${originY}px`,
    width: `${THEME_PING_BASE_SIZE_PX}px`,
    height: `${THEME_PING_BASE_SIZE_PX}px`,
    borderRadius: '999px',
    border: `1.5px solid ${getThemePingColor(nextTheme)}`,
    boxSizing: 'border-box',
    transform: 'translate(-50%, -50%) scale(0.35)',
    opacity: '0.92',
    pointerEvents: 'none',
    zIndex: '9998',
    boxShadow: `0 0 18px ${getThemePingColor(nextTheme)}33`,
  });
  document.body.appendChild(ping);

  function settlePing() {
    ping.remove();
    onComplete();
  }

  if (typeof ping.animate !== 'function') {
    settlePing();
    return;
  }

  const anim = ping.animate(
    [
      { transform: 'translate(-50%, -50%) scale(0.35)', opacity: 0.92 },
      { transform: `translate(-50%, -50%) scale(${targetScale})`, opacity: 0 },
    ],
    { duration: THEME_PING_DURATION_MS, easing: 'cubic-bezier(0.16, 0.84, 0.32, 1)', fill: 'forwards' }
  );

  anim.onfinish = settlePing;
  anim.oncancel = settlePing;
}

function getPreferred() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) return stored;
  return 'dark';
}

function applyTheme(theme) {
  document.documentElement.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute(
    'content',
    theme === 'dark' ? DARK_THEME_COLOR : LIGHT_THEME_COLOR
  );
  updateCanvasTheme(theme === 'dark');
}

function revealToggle(nextTheme) {
  if (isThemeTransitioning) return;

  const btn   = document.getElementById('theme-toggle');
  if (!btn) return;

  isThemeTransitioning = true;

  const rect  = btn.getBoundingClientRect();
  const ox    = Math.round(rect.left + rect.width  / 2);
  const oy    = Math.round(rect.top  + rect.height / 2);
  applyTheme(nextTheme);
  localStorage.setItem(STORAGE_KEY, nextTheme);
  launchThemePing(ox, oy, nextTheme, () => {
    isThemeTransitioning = false;
  });
}

export function initTheme() {
  const theme = getPreferred();
  applyTheme(theme);

  const btn = document.getElementById('theme-toggle');
  if (!btn) return;

  btn.addEventListener('click', () => {
    const current = document.documentElement.dataset.theme;
    revealToggle(current === 'dark' ? 'light' : 'dark');
  });
}
